import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { IMAGE_URL, TABLE_DATE_FORMAT, TABLE_DATE_TIME_FORMAT, TABLE_ROWS_PER_PAGE, TABLE_TIME_FORMAT } from "../../../utils/Constants";
import Str from "../../common/Str";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import JsPDF from "jspdf";
import "jspdf-autotable";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import Swal from "sweetalert2";
import moment from "moment";
import {
  GetAssemblyResourceAuditTrailCancelToken,
  GetAssemblyResourceAuditTrailDropdownCancelToken,
} from "../../../services/CourseService";
import { Link, useParams } from "react-router-dom";
import Hover from "../../common/Hover";
import { TrimText } from "../../common/TrimText";
import { InitialRender } from "../../common/Helper";
import axios from "axios";
import { checkIfImageExists, handleTableScroll } from "../../../utils/commonFunction";
import { RenderAuditTrailActionType } from "../../../utils/CommonGroupingItem";
import useDebounce from "../../../hooks/useDebounce";

const EXPORT_HEADER = [
  "User", "Action Type", "Resource Type", "Resource Name", "Old Value", "New Value", "Date",
];

/**
 * Parses audit JSON from a string or returns the value when already parsed.
 * @param {string|object|null|undefined} value - Raw audit field value.
 * @returns {object|null} Parsed object or null when empty/invalid.
 */
const parseAuditJson = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Resolves the old definition for an audit row from payload or description.
 * @param {object} row - Normalized audit trail row.
 * @returns {object} Old definition object.
 */
const getDefinitionOld = (row) => {
  if (row?.old_data !== null && row?.old_data !== undefined && row?.old_data !== "") {
    return parseAuditJson(row.old_data) || {};
  }
  return row?.description?.object?.definition?.Old || {};
};

/**
 * Resolves the new definition for an audit row from payload or description.
 * @param {object} row - Normalized audit trail row.
 * @returns {object} New definition object.
 */
const getDefinitionNew = (row) => {
  if (row?.new_data !== null && row?.new_data !== undefined && row?.new_data !== "") {
    return parseAuditJson(row.new_data) || {};
  }
  return row?.description?.object?.definition?.New || {};
};

/**
 * Returns the display name for an audit resource.
 * @param {object} row - Normalized audit trail row.
 * @returns {string} Resource name or "-".
 */
const getAuditResourceName = (row) => {
  if (row?.resource_name) return row.resource_name;

  const linkedFromNewData = parseAuditJson(row?.new_data)?.linkAssemblyData;
  if (linkedFromNewData?.length) {
    const linkedNames = linkedFromNewData
      .map((item) => item?.titleName || item?.courseName)
      .filter(Boolean);
    if (linkedNames.length) {
      return [...new Set(linkedNames)].join(", ");
    }
  }

  const action = row?.description?.verb?.Action;
  if (action === "Deleted" || action === "Unlinked Course") {
    return (
      row?.description?.object?.definition?.Old?.name
      || row?.description?.object?.definition?.Old?.old_assemblies?.name
      || "-"
    );
  }

  return row?.description?.object?.definition?.New?.name || "-";
};

/**
 * Maps a resource type value to its display label.
 * @param {string|null|undefined} resourceType - Raw resource type.
 * @returns {string} Human-readable resource type label.
 */
const renderAuditResourceType = (resourceType) => {
  if (resourceType === "resource") return "Resource";
  if (resourceType === "assemblies") return "Assemblies";
  if (resourceType === "Assembly" || resourceType === "assembly") return "Assembly";
  if (resourceType?.toLowerCase() === "resource pool") return "Resource Pool";
  if (resourceType === "Course") return "Course";
  return "Assigned Resource";
};

/**
 * Returns the audit action from description or action_type fallback.
 * @param {object} row - Normalized audit trail row.
 * @returns {string} Action label.
 */
const getAuditAction = (row) =>
  row?.description?.verb?.Action || row?.action_type || "";

/**
 * Returns true when the audit action should not expose a resource link.
 * @param {object} row - Normalized audit trail row.
 * @returns {boolean}
 */
const isDeletedAuditAction = (row) => getAuditAction(row) === "Deleted";

/**
 * Formats an audit entry value for table display with truncation.
 * @param {*} value - Entry value to format.
 * @returns {string} Display string.
 */
const formatAuditEntryValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const text =
    typeof value === "object"
      ? JSON.stringify(value)
      : value.toString();
  return text.length > 30 ? `${text.slice(0, 30)}...` : text;
};

/**
 * Returns the CSS class for a resource type badge.
 * @param {string|null|undefined} resourceType - Raw resource type.
 * @returns {string} CSS class name.
 */
const getResourceTypeClassName = (resourceType) => {
  if (resourceType === "resource") return "as-widget as-green-bg";
  if (
    resourceType === "assembly" ||
    resourceType === "Assembly" ||
    resourceType === "assemblies"
  ) {
    return "as-widget as-red-bg";
  }
  if (resourceType?.toLowerCase() === "resource pool") return "as-widget as-yellow-bg";
  if (resourceType === "Course") return "as-widget as-red-bg";
  return "as-widget as-blue-bg";
};

/**
 * Compares two objects and returns keys whose values differ.
 * @param {object} obj1 - First object.
 * @param {object} obj2 - Second object.
 * @returns {string[]} List of changed key/value strings.
 */
const difference = (obj1, obj2) => {
  const keyFound = [];
  const safeObj1 = obj1 && typeof obj1 === "object" ? obj1 : {};
  const safeObj2 = obj2 && typeof obj2 === "object" ? obj2 : {};

  Object.keys(safeObj1).forEach((key) => {
    if (Array.isArray(safeObj1[key]) && Array.isArray(safeObj2[key])) {
      safeObj1[key].forEach((item, index) => {
        if (JSON.stringify(item) !== JSON.stringify(safeObj2[key][index])) {
          keyFound.push(`${key}:${JSON.stringify(item)}`);
        }
      });
    } else if ((safeObj1[key] ? safeObj1[key] : "") !== (safeObj2[key] ? safeObj2[key] : "")) {
      keyFound.push(`${key}:${safeObj1[key]}`);
    }
  });

  return keyFound;
};

/**
 * Returns the best available timestamp for an audit row.
 * @param {object} row - Audit trail row.
 * @returns {string|null|undefined} Timestamp value.
 */
const getAuditRowTimestamp = (row) =>
  row?.date_time || row?.description?.timestamp || row?.created_at || null;

/**
 * Sorts audit rows by timestamp descending.
 * @param {object} rowA - First row.
 * @param {object} rowB - Second row.
 * @returns {number} Sort order.
 */
const sortingDate = (rowA, rowB) => {
  const date1 = new Date(getAuditRowTimestamp(rowA));
  const date2 = new Date(getAuditRowTimestamp(rowB));

  if (date2 > date1) return 1;
  if (date2 < date1) return -1;
  return 0;
};

/**
 * Sorts audit rows by resource name.
 * @param {object} rowA - First row.
 * @param {object} rowB - Second row.
 * @returns {number} Sort order.
 */
const sortingName = (rowA, rowB) => {
  const name1 = getAuditResourceName(rowA)?.toString().trim();
  const name2 = getAuditResourceName(rowB)?.toString().trim();

  if (name1 && name2) {
    return name1.localeCompare(name2);
  }
  return 0;
};

/**
 * Sorts audit rows by user name.
 * @param {object} rowA - First row.
 * @param {object} rowB - Second row.
 * @returns {number} Sort order.
 */
const sortingUser = (rowA, rowB) => {
  const name1 = rowA?.description?.User?.name?.toString() || "";
  const name2 = rowB?.description?.User?.name?.toString() || "";
  return name1.localeCompare(name2);
};

/**
 * Formats old value text for export.
 * @param {object} row - Audit trail row.
 * @returns {string} Old value export string.
 */
const oldValue = (row) => {
  const oldDef = getDefinitionOld(row);
  const newDef = getDefinitionNew(row);
  const entries = Object.entries(oldDef);

  if (getAuditAction(row) === "Edited") {
    const diff = difference(oldDef, newDef);
    return diff.length !== 0 ? `${diff.sort()}` : "NA";
  }

  return `${entries.map((entry) => `${entry[0]}: ${entry[1] !== null && entry[1] !== undefined ? (typeof entry[1] === "object" ? JSON.stringify(entry[1]) : entry[1]) : "N/A"}`)}`;
};

/**
 * Formats new value text for export.
 * @param {object} row - Audit trail row.
 * @returns {string} New value export string.
 */
const NewValue = (row) => {
  const oldDef = getDefinitionOld(row);
  const newDef = getDefinitionNew(row);
  const entries = Object.entries(newDef);

  if (getAuditAction(row) === "Edited") {
    const diff = difference(newDef, oldDef);
    return diff.length !== 0 ? `${diff.sort()}` : "NA";
  }

  if (getAuditAction(row) === "Profile Picture Edited") {
    return newDef?.picture_me?.toString().length > 30
      ? `picture_me:${newDef?.picture_me}`
      : newDef?.picture_me;
  }

  return `${entries.map((entry) => `${entry[0]}: ${entry[1] !== null && entry[1] !== undefined ? (typeof entry[1] === "object" ? JSON.stringify(entry[1]) : entry[1]) : "N/A"}`)}`;
};

/**
 * Normalizes raw audit trail API rows for table consumption.
 * @param {Array<object>|null|undefined} arr - Raw audit trail rows.
 * @returns {Array<object>} Normalized rows.
 */
const normalizeAuditTrailRows = (arr) => {
  const tempArr = [];

  (arr || []).forEach((item) => {
    try {
      let description = {};
      if (typeof item.description === "string") {
        description = JSON.parse(item.description);
      } else if (item.description && typeof item.description === "object") {
        description = item.description;
      }

      tempArr.push({
        id: item.id,
        action_id: item.action_id,
        rel_id: item.rel_id,
        description,
        resource_type: item.resource_type,
        new_data: item.new_data,
        old_data: item.old_data,
        resource_name: item.resource_name,
        resource_link: item.resource_link,
        action_type: item.action_type,
        user: item.user,
        date_time: item.date_time,
        created_at: item.created_at,
        authFullName: item.authFullName,
      });
    } catch {
      // Skip rows with invalid description JSON.
    }
  });

  return tempArr;
};

/**
 * Toggles a value in a filter array immutably.
 * @param {Array<string>} currentValues - Current selected values.
 * @param {string} value - Value to toggle.
 * @returns {Array<string>} Updated values.
 */
const toggleFilterValue = (currentValues, value) =>
  currentValues.includes(value)
    ? currentValues.filter((item) => item !== value)
    : [...currentValues, value];

const SORT_KEY_MAP = {
  timestamp: "date_time",
  user: "user",
  Name: "resource_name",
};

/**
 * Resolves audit trail payload from API response body.
 * @param {object|null|undefined} responseData - API response data.
 * @returns {object|Array|null} Audit trail payload.
 */
const getAuditTrailPayload = (responseData) => {
  if (!responseData) return null;

  if (Array.isArray(responseData.data) && responseData.total != null) {
    return responseData;
  }

  if (responseData.audit_trail) {
    return responseData.audit_trail;
  }

  return responseData;
};

/**
 * Extracts rows and pagination metadata from audit trail responses.
 * @param {*} auditTrail - API audit_trail payload.
 * @returns {{ rows: Array<object>, total: number, page: number, perPage: number, lastPage: number }} Parsed list metadata.
 */
const parseAuditTrailResponse = (auditTrail) => {
  if (Array.isArray(auditTrail)) {
    return {
      rows: auditTrail,
      total: auditTrail.length,
      page: 1,
      perPage: auditTrail.length,
      lastPage: 1,
    };
  }

  return {
    rows: auditTrail?.data || [],
    total: auditTrail?.total || 0,
    page: auditTrail?.page || 1,
    perPage: auditTrail?.perPage || 10,
    lastPage: auditTrail?.lastPage || 1,
  };
};

/**
 * Assembly audit trail table with search, filters, and export.
 * @returns {JSX.Element} Audit trail view.
 */
function AuditTrail() {
  const { id, tab, subType } = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [ticketData, setTicketData] = useState([]);
  const [actiontype, setActionType] = useState([]);
  const [searchActionTypeCheck, setSearchActionTypeCheck] = useState({});
  const [resourceType, setResourceType] = useState([]);
  const [searchResourceTypeCheck, setSearchResourceTypeCheck] = useState({});
  const [month, setMonth] = useState([]);
  const [searchMonthCheck, setSearchMonthCheck] = useState({});
  const [year, setYear] = useState([]);
  const [searchYearCheck, setSearchYearCheck] = useState({});
  const [isLoaded, setIsLoaded] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [sortkey, setSortKey] = useState("date_time");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [yearList, setYearList] = useState([]);
  const [monthList, setMonthList] = useState([]);
  const [actionTypeList, setActionTypeList] = useState([]);
  const [resourceTypeList, setResourceTypeList] = useState([]);

  useEffect(() => {
    handleTableScroll();
  }, [isLoaded]);

  const buildRequestParams = useCallback((exportStatus = "false") => ({
    viaActionType: actiontype,
    resource_type: resourceType,
    viaYears: year,
    viaMonths: month,
    page,
    limit: perPage,
    key: sortkey,
    sort: sortOrder,
    search: debouncedSearch,
    exportStatus,
    ...(tab === "assemblies" && subType === "audit-trail" && id ? { action_id: id } : {}),
  }), [actiontype, resourceType, year, month, page, perPage, sortkey, sortOrder, debouncedSearch, tab, subType, id]);

  const getDropdownParams = useCallback(() => {
    if (tab === "audit-trail") {
      return {};
    }
    if ((tab === "assemblies" || tab === "resource-pool") && subType === "audit-trail" && id) {
      return { action_id: id };
    }
    return null;
  }, [tab, subType, id]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const dropdownSource = axios.CancelToken.source();

    const fetchDropdown = async () => {
      const dropdownParams = getDropdownParams();
      if (!dropdownParams) return;

      try {
        const res = await GetAssemblyResourceAuditTrailDropdownCancelToken(dropdownParams, dropdownSource.token);
        setYearList(res?.data?.getYears || []);
        setMonthList(res?.data?.getMonths || []);
        setActionTypeList(res?.data?.getActionType || []);
        setResourceTypeList(res?.data?.getResourceType || []);
      } catch (error) {
        if (!axios.isCancel(error)) {
          setYearList([]);
          setMonthList([]);
          setActionTypeList([]);
          setResourceTypeList([]);
        }
      }
    };

    fetchDropdown();

    return () => {
      dropdownSource.cancel("Component unmounted");
    };
  }, [getDropdownParams]);

  useEffect(() => {
    const cancelTokenSources = [];
    setIsLoaded(true);

    const getData = async () => {
      cancelTokenSources.forEach((source) => {
        source.cancel("New request made");
      });

      const source = axios.CancelToken.source();
      cancelTokenSources.push(source);

      try {
        const res = await GetAssemblyResourceAuditTrailCancelToken(
          buildRequestParams("false"),
          source.token
        );
        const payload = getAuditTrailPayload(res?.data);
        const { rows, total } = parseAuditTrailResponse(payload);
        setTicketData(normalizeAuditTrailRows(rows));
        setTotalRows(total);
        if (res.status === 200) {
          setIsLoaded(false);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          setIsLoaded(false);
        }
      }
    };

    getData();

    return () => {
      cancelTokenSources.forEach((source) => {
        source.cancel("Component unmounted");
      });
    };
  }, [buildRequestParams]);

  const sortingDate = (rowA, rowB) => {
    const date1 = new Date(rowA?.description?.timestamp);
    const date2 = new Date(rowB?.description?.timestamp);

    if (date2 > date1) {
      return 1;
    } else if (date2 < date1) {
      return -1;
    } else {
      return 0;
    }
  };

  const sortingName = (rowA, rowB) => {
    const name1 = rowA?.description?.object?.definition?.New?.name ? rowA?.description?.object?.definition?.New?.name?.toString().trim().toLowerCase() : rowA?.description?.object?.definition?.Old?.name?.toString().trim().toLowerCase();
    const name2 = rowB?.description?.object?.definition?.New?.name ? rowB?.description?.object?.definition?.New?.name?.toString().trim().toLowerCase() : rowB?.description?.object?.definition?.Old?.name?.toString().trim().toLowerCase();
    
    if(name1 && name2){
      return name1.localeCompare(name2);
    }else{
      return 0;
    }
  };

  const sortingUser = (rowA, rowB) => {
    const name1 = rowA.description?.User?.name?.trim().toLowerCase();
    const name2 = rowB.description?.User?.name?.trim().toLowerCase();
    return name1.localeCompare(name2);
  };

  const difference = (obj1, obj2) => {
    let keyFound = [];
    Object.keys(obj1).forEach((key) => {
      if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
        obj1[key].forEach((item, index) => {
          if (JSON.stringify(item) !== JSON.stringify(obj2[key][index])) {
            keyFound.push(key + ":" + JSON.stringify(item));
          }
        });
      } else if ((obj1[key] ? obj1[key] : "") !== (obj2[key] ? obj2[key] : "")) {
        return keyFound.push(key + ":" + obj1[key]);
      }
    });
    return keyFound;
  };


  const columns = useMemo(() => [
    {
      name: "User",
      selector: "user",
      sortable: true,
      sortFunction: sortingUser,
      cell: (row) => {
        const user = row?.description?.User || {};
        const userName = row?.authFullName || row?.user || user?.name || "-";
        const nameParts = userName.split(" ");
        const userPhoto = (user?.URL || "")
          .replace("/home/myaie/public_html/", "")
          .replace("public/", "");

        return (
          <div className="assigned-title-block-new">
            <div className="assigned-title-itm">
              <span className="assigned-title-blk name-icon">
                {user?.URL && checkIfImageExists(`${IMAGE_URL}/${userPhoto}`) ? (
                  <img
                    src={`${IMAGE_URL}/${userPhoto}`}
                    alt="AIE"
                  />
                ) : (
                  InitialRender([
                    nameParts[0] || "-",
                    nameParts[1] || "",
                  ])
                )}

                <span
                  className={`profile-box-2-status ${user?.status
                    ? user.status
                    : "Offline"
                    }`}
                >
                  <i className="fas fa-circle"></i>
                </span>

                <Hover
                  firstName={nameParts[0] || "-"}
                  lastName={nameParts[1] || ""}
                  photo={userPhoto}
                  email={user?.mbox}
                  mobile={user?.Mobile}
                  status={!user?.status || user.status === "offline" ? false : true}
                  right={true}
                  role={"module"}
                />
              </span>
              <p>
                <b>{userName}</b>
              </p>
            </div>
          </div>
        );
      },
    },
    {
      name: "Action Type",
      cell: (row) => RenderAuditTrailActionType(getAuditAction(row)).html,
    },
    {
      name: "Resource Type",
      cell: (row) => (
        <div className={getResourceTypeClassName(row.resource_type)}>
          {renderAuditResourceType(row.resource_type)}
        </div>
      ),
    },
    {
      name: "Resource Name",
      selector: "Name",
      sortable: true,
      sortFunction: sortingName,
      cell: (row) => {
        const resourceName = getAuditResourceName(row);
        const resourceLink = row.resource_link || parseAuditJson(row?.new_data)?.redirect_link;
        const action = getAuditAction(row);
        const disableResourceLink = isDeletedAuditAction(row);

        return (
        <div className="assigned-title-block-new">
          <div className="assigned-title-itm">
            {resourceName && resourceName !== "-" ? (
              !disableResourceLink && resourceLink ? (
                <Link
                  title={resourceName}
                  className="as-text-blue"
                  to={resourceLink}
                >
                  {TrimText(resourceName, 30)}
                </Link>
              ) : (
                <p title={resourceName} className="">
                  {TrimText(resourceName, 30)}
                </p>
              )
            ) : (
              <>
                {action === "Deleted" && (
                  <p
                    title={row?.description?.object?.definition?.Old?.name || TrimText(row?.description?.object?.definition?.Old?.old_assemblies?.name, 30) || "-"}
                    className=""
                  >
                    {TrimText(row?.description?.object?.definition?.Old?.name, 30) || TrimText(row?.description?.object?.definition?.Old?.old_assemblies?.name, 30) || "-"}
                  </p>
                )}
                {action === "Unlinked Course" && (
                  <Link
                    title={row?.description?.object?.definition?.Old?.name}
                    className="as-text-blue"
                    to={row?.description?.object?.definition?.Old?.redirect_link}
                  >
                    {TrimText(row?.description?.object?.definition?.Old?.name, 30)}
                  </Link>
                )}
                {action !== "Deleted" && action !== "Unlinked Course" && (
                  <Link
                    title={row?.description?.object?.definition?.New?.name}
                    className="as-text-blue"
                    to={row.resource_type === "resource" ? `/courses/content/assemblies/assignedResources/${row.rel_id}` : `/courses/content/assemblies/edit/${row?.action_id}`}
                  >
                    {TrimText(row?.description?.object?.definition?.New?.name, 30)}
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
        );
      },
    },
    {
      name: "Old Value",
      cell: (row) => {
        const oldDef = getDefinitionOld(row);
        const newDef = getDefinitionNew(row);
        const entries = Object.entries(oldDef);

        if (row?.description?.verb?.Action === "Created") {
          return "-";
        }

        if (row?.description?.verb?.Action === "Edited") {
          const diff = difference(oldDef, newDef);
          return diff?.length !== 0
            ? (
              <div className="tooltip-custom" title={`${diff.sort().map((entry) => entry.toString())}`.split(",").join("\n")}>
                {diff.sort().map((entry, index) => (
                  index < 2 && (
                    <p key={index}>
                      {entry.toString().length > 30 ? `${entry.toString().slice(0, 30)}...` : entry}
                    </p>
                  )
                ))}
                {diff.length > 2 ? "..." : null}
              </div>
            )
            : "N/A";
        }

        return (
          <div
            className="tooltip-custom h"
            title={`${Object.keys(oldDef).sort().map((entry) => `${entry.toString()} : ${JSON.stringify(oldDef[entry])}`)}`.split(",").join("\n")}
          >
            {entries && entries.length > 0 ? entries.slice(0, 2).map((entry, index) => (
              <p key={index}>
                {entry[0]} : {formatAuditEntryValue(entry[1])}
              </p>
            )) : "N/A"}
          </div>
        );
      },
    },
    {
      name: "New Value",
      cell: (row) => {
        const oldDef = getDefinitionOld(row);
        const newDef = getDefinitionNew(row);
        const entries = Object.entries(newDef);

        if (getAuditAction(row) === "Edited") {
          const diff = difference(newDef, oldDef);
          return diff.length !== 0
            ? (
              <div className="tooltip-custom" title={`${diff.sort().map((entry) => entry.toString())}`.split(",").join("\n")}>
                {diff.sort().map((entry, index) => (
                  index < 2 && (
                    <p key={index}>
                      {entry.toString().length > 30 ? `${entry.toString().slice(0, 30)}...` : entry}
                    </p>
                  )
                ))}
                {diff.length > 2 ? "..." : null}
              </div>
            )
            : "N/A";
        }

        return (
          <div className="tooltip-custom" title={`${entries.map((entry) => `${entry[0]}:${entry[1] !== null && entry[1] !== undefined ? (typeof entry[1] === "object" ? JSON.stringify(entry[1]) : entry[1]) : "N/A"}`)}`.split(",").join("\n")}>
            {entries && entries.length > 0 ? entries.slice(0, 2).map((entry, index) => (
              <p key={index}>
                {entry[0]} : {formatAuditEntryValue(entry[1])}
              </p>
            )) : "N/A"}
            {entries.length > 2 ? "..." : null}
          </div>
        );
      },
    },
    {
      name: "Date",
      sortable: true,
      selector: "timestamp",
      sortFunction: sortingDate,
      cell: (row) => {
        const timestamp = getAuditRowTimestamp(row);
        return timestamp ? (
          <div className="dateTime" title={`${moment(timestamp).format(`${TABLE_DATE_FORMAT},`)} ${moment(timestamp).format(TABLE_TIME_FORMAT)}`}>
            <p className="right-space">{moment(timestamp).format(`${TABLE_DATE_FORMAT},`)}</p>
            <p>{moment(timestamp).format(TABLE_TIME_FORMAT)}</p>
          </div>
        ) : (
          "NA"
        );
      },
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="assessment-08">
          <div className="as-buttons">
            <button
              className="btn btn-primary rounded-circle"
              title="Open"
              type="button"
              onClick={() => {
                Swal.fire({ html: `<div className="audit-trial-json"><pre>${JSON.stringify(row.description, undefined, 1)}</pre></div>` });
              }}
            >
              <i className="fal fa-folder-open"></i>
            </button>
          </div>
        </div>
      ),
    },
  ],[]);

  const handleSearchFilter = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleActionTypeFilter = (e) => {
    const { value } = e.target;
    setActionType((prev) => toggleFilterValue(prev, value));
    setSearchActionTypeCheck((prev) => ({ ...prev, [value]: !prev[value] }));
    setPage(1);
  };

  const handleResourceTypeFilter = (e) => {
    const { value } = e.target;
    setResourceType((prev) => toggleFilterValue(prev, value));
    setSearchResourceTypeCheck((prev) => ({ ...prev, [value]: !prev[value] }));
    setPage(1);
  };

  const handleMonthFilter = (e) => {
    const { value } = e.target;
    setMonth((prev) => toggleFilterValue(prev, value));
    setSearchMonthCheck((prev) => ({ ...prev, [value]: !prev[value] }));
    setPage(1);
  };

  const handleYearFilter = (e) => {
    const { value } = e.target;
    setYear((prev) => toggleFilterValue(prev, value));
    setSearchYearCheck((prev) => ({ ...prev, [value]: !prev[value] }));
    setPage(1);
  };

  const handleSort = (column, sortDirection) => {
    setSortKey(SORT_KEY_MAP[column.selector] || column.selector || "date_time");
    setSortOrder(sortDirection === "asc" ? "ASC" : "DESC");
    setPage(1);
  };

  const handlePageChange = (pageNo) => {
    setPage(pageNo);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const resetFilter = () => {
    setSearchTerm("");
    setActionType([]);
    setSearchActionTypeCheck({});
    setMonth([]);
    setSearchMonthCheck({});
    setYear([]);
    setSearchYearCheck({});
    setResourceType([]);
    setSearchResourceTypeCheck({});
    setPage(1);
  };

  const mapExportRow = (row) => ({
    ...row,
    User: row?.authFullName || row?.user || row?.description?.User?.name || "-",
    "Action Type": getAuditAction(row) || "-",
    "Resource Type": renderAuditResourceType(row.resource_type),
    "Resource Name": getAuditResourceName(row),
    "Old Value": Object.keys(getDefinitionOld(row)).length !== 0 ? oldValue(row) : "NA",
    "New Value": Object.keys(getDefinitionNew(row)).length !== 0 ? NewValue(row) : "NA",
    Date: getAuditRowTimestamp(row) ? new Date(getAuditRowTimestamp(row)).toLocaleDateString() : "-",
  });

  const renderActionTypeUI = () => actionTypeList.map((item) => (
    <li key={item.value}>
      <input
        type="checkbox"
        id={`action-${item.value}`}
        value={item.value}
        onClick={handleActionTypeFilter}
        checked={!!searchActionTypeCheck[item.value]}
      />
      <label htmlFor={`action-${item.value}`}>
        {RenderAuditTrailActionType(item.label || item.value).html}
      </label>
    </li>
  ));

  const renderResourceTypeUI = () => resourceTypeList.map((item) => (
    <li key={item.value}>
      <input
        type="checkbox"
        id={`resource-${item.value}`}
        value={item.value}
        onClick={handleResourceTypeFilter}
        checked={!!searchResourceTypeCheck[item.value]}
      />
      <label htmlFor={`resource-${item.value}`}>
        <span className={getResourceTypeClassName(item.value)}>
          {renderAuditResourceType(item.label || item.value)}
        </span>
      </label>
    </li>
  ));

  const renderMonthUI = () => monthList.map((item) => (
    <li key={item.value}>
      <input
        type="checkbox"
        id={`month-${item.value}`}
        value={item.value}
        onClick={handleMonthFilter}
        checked={!!searchMonthCheck[item.value]}
      />
      <label htmlFor={`month-${item.value}`}>
        <span>{item.label}</span>
      </label>
    </li>
  ));

  const renderYearUI = () => yearList.map((item) => (
    <li key={item.value}>
      <input
        type="checkbox"
        id={`year-${item.value}`}
        value={item.value}
        onClick={handleYearFilter}
        checked={!!searchYearCheck[item.value]}
      />
      <label htmlFor={`year-${item.value}`}>
        <span>{item.label}</span>
      </label>
    </li>
  ));

  const exportData = async (fileType, fileName) => {
    try {
      const res = await GetAssemblyResourceAuditTrailCancelToken(buildRequestParams("true"));
      const payload = getAuditTrailPayload(res?.data);
      const { rows } = parseAuditTrailResponse(payload);
      const data = normalizeAuditTrailRows(rows).map(mapExportRow);

      if (fileType === "csv") {
        const csvString = Papa.unparse({ fields: EXPORT_HEADER, data });
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8," });
        const blobURL = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.download = fileName;
        anchor.href = blobURL;
        anchor.dataset.downloadurl = ["text/csv", anchor.download, anchor.href].join(":");
        anchor.click();

        setTimeout(() => {
          URL.revokeObjectURL(blobURL);
        }, 1000);
        return;
      }

      if (fileType === "xlsx") {
        const compatibleData = data.map((row) => {
          const obj = {};
          EXPORT_HEADER.forEach((col) => {
            obj[col] = row[col];
          });
          return obj;
        });

        const wb = XLSX.utils.book_new();
        const ws1 = XLSX.utils.json_to_sheet(compatibleData, { header: EXPORT_HEADER });
        XLSX.utils.book_append_sheet(wb, ws1, "React Table Data");
        XLSX.writeFile(wb, `${fileName}.xlsx`);
        return;
      }

      if (fileType === "pdf") {
        const compatibleData = data.map((row) => [
          row.User, row["Action Type"], row["Resource Type"], row["Resource Name"],
          row["Old Value"], row["New Value"], row.Date,
        ]);
        const doc = new JsPDF();
        doc.autoTable({
          head: [EXPORT_HEADER],
          body: compatibleData,
          columnStyles: {
            0: { columnWidth: 20 },
            1: { columnWidth: 24 },
            2: { columnWidth: 24 },
            3: { columnWidth: 24 },
            4: { columnWidth: 40 },
            5: { columnWidth: 40 },
            6: { columnWidth: 20 },
          },
          styles: {
            minCellHeight: 10,
            minCellWidth: 5,
            halign: "left",
            fontSize: 8,
          },
        });
        doc.save(`${fileName}.pdf`);
      }
    } catch {
      // Export failed silently to avoid breaking UI flow.
    }
  };

  return (
          <div className="my-tickets-info-list Tickets-main-wrap">
            <div className="l-o-c-t custom-table-div filter-search-icon card card-table-custom">
              <div className="search-filter-div">
                <div className="search-filter-div-left">
                <div className="system-administration-table table-responsive">
                <div className="table-responsive-div">
                  <div id="assessment-table-main_wrapper" className="dataTables_wrapper no-footer">
                    <div id="assessment-table-main_wrapper" className="dataTables_wrapper no-footer">
                      <div id="assessment-table-main_filter" className="dataTables_filter">
                        <label>
                          <input type="search" placeholder="Search" aria-controls="assessment-table-main" onChange={handleSearchFilter} value={searchTerm} />
                        </label>
                        <div className="filter-eff filter-data-btn">
                          <button className="filter-buttons" type="button">
                          <i className="fal fa-filter"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="filter-button-group">
                  <div className="filter-scroll">
                      <div className={`filter-scroll-inner  filter-custom-new`}>
                        <div className="assessment-table-filter">
                          <div className="btn-type-filter dropdown-comman">
                            <div className="dropdown multiselect">
                              <button className={`btn btn-default dropdown-toggle ${actiontype.length ? "btn-selected" : ""}`} title="Action Type" type="button" id="dropdownMenu1" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" >
                                <span>
                                  Action Type <i className="fal fa-angle-down"></i>
                                </span>
                              </button>
                              <form className="dropdown-menu remove-list-style">
                                <ul aria-labelledby="dropdownMenu1">
                                  {renderActionTypeUI()}
                                </ul>
                              </form>
                            </div>
                          </div>
                        </div>

                        <div className="assessment-table-filter">
                          <div className="btn-type-filter dropdown-comman">
                            <div className="dropdown multiselect">
                              <button className={`btn btn-default dropdown-toggle ${resourceType.length ? "btn-selected" : ""}`} title="Resource Type" type="button" id="dropdownMenu2" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" >
                                <span>
                                  Resource Type <i className="fal fa-angle-down"></i>
                                </span>
                              </button>
                              <form className="dropdown-menu remove-list-style">
                                <ul aria-labelledby="dropdownMenu2">
                                  {renderResourceTypeUI()}
                                </ul>
                              </form>
                            </div>
                          </div>
                        </div>

                        <div className="assessment-table-filter">
                          <div className="btn-type-filter dropdown-comman">
                            <div className="dropdown multiselect">
                              <button className={`btn btn-default dropdown-toggle ${month.length ? "btn-selected" : ""}`} type="button" title="Month" id="dropdownMenu1" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" >
                                <span>
                                  Month <i className="fal fa-angle-down"></i>
                                </span>
                              </button>
                              <form className="dropdown-menu remove-list-style">
                                <ul aria-labelledby="dropdownMenu1">{renderMonthUI()}</ul>
                              </form>
                            </div>
                          </div>
                        </div>

                        <div className="assessment-table-filter">
                          <div className="btn-type-filter dropdown-comman">
                            <div className="dropdown multiselect">
                              <button className={`btn btn-default dropdown-toggle ${year.length ? "btn-selected" : ""}`} type="button" id="dropdownMenu1" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" title="Year" >
                                <span>
                                  Year <i className="fal fa-angle-down"></i>
                                </span>
                              </button>
                              <form className="dropdown-menu remove-list-style">
                                <ul aria-labelledby="dropdownMenu1">{renderYearUI()}</ul>
                              </form>
                            </div>
                          </div>
                        </div>
                      </div>
                  </div>
                  <div className="reset-btn-group">
                    <div className="button-reset dropdown-comman">
                      <button className="btn btn-primary" onClick={resetFilter} title="Reset" >
                        <i className="fal fa-redo"></i>Reset
                      </button>
                    </div>

                    <div className="files-export-group">
                      <button
                        type="button"
                        className="btn btn-files"
                        onClick={() => {
                          exportData("xlsx", "Content_Assembly_Audit_Trail");
                        }}
                        title="Export spreadsheet"
                      >
                        <i className="fal fa-file-excel icon"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-files"
                        onClick={() => {
                          exportData("csv", "Content_Assembly_Audit_Trail");
                        }}
                        title="Export CSV"
                      >
                        <i className="fal fa-file-csv icon"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-files"
                        onClick={() => {
                          exportData("pdf", "Content_Assembly_Audit_Trail");
                        }}
                        title="Export PDF"
                      >
                        <i className="fal fa-file-pdf icon"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
                  </div>
               
                </div>
              
              {isLoaded ? (
                <SkeletonTicketList />
              ) : (
                <DataTable
                  data={ticketData}
                  defaultSortField="timestamp"
                  defaultSortAsc={false}
                  onSort={handleSort}
                  columns={columns}
                  pagination={true}
                  paginationServer
                  sortServer
                  paginationDefaultPage={page}
                  paginationPerPage={perPage}
                  paginationTotalRows={totalRows}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handlePerRowsChange}
                  noDataComponent={Str.noRecord}
                  paginationRowsPerPageOptions={TABLE_ROWS_PER_PAGE}
                />
              )}
            </div>
          </div>
  );
}

export default AuditTrail;
